import { XbotBadge, XbotCancelledEvent, XbotErrorBody, XbotMessage, XbotMessageBlock, XbotWindowLabel } from "@dto/pyxbot-agent.dto";
import { Button } from "antd";
import { upperFirst } from "lodash-es";
import { FC, useState } from "react";
import { LuRotateCcw } from "react-icons/lu";
import { isEmptyTable } from "./blocks/TableBlock";
import { XbotBlockHost, XbotCallbacks } from "./host";
import { SaidWindowContext } from "./parts";
import { CalloutHead, claimsOf, DATA_BLOCK_TYPES, TONE_HEAD, toneOfBlock, XbotCallout, XbotTone } from "./tone";
import { outcomeOfMessage, STOP_REASON, STOPPED_SENTENCE, stopReasonOf, XbotTurnOutcome, XbotTurnProgress } from "./turnState";
import { errorText, registryFor, XbotBlockView } from "./XbotBlockView";
import { XbotProgressBar } from "./XbotProgressBar";
import "./xbot.less";

export const EMPTY_ANSWER_TEXT = "The answer came back empty.";

/** What to ask instead when an answer was stopped for being too big. */
export const ASK_SMALLER_TEXT = "Ask for a shorter period or fewer objects.";

/** How an answer that is not one closes: its head, what happened, what to try. */
export interface EndingNotice {
  tone: XbotTone;
  stopped: boolean;
  head: string;
  text: string;
  hint: string;
  /** Asking the same question again could help: not after a newer question replaced it. */
  retry: boolean;
}

export function endingNotice(
  outcome: XbotTurnOutcome,
  error: XbotErrorBody | null | undefined,
  cancelled: XbotCancelledEvent | null | undefined,
  empty: boolean,
  message: XbotMessage | null = null,
): EndingNotice | null {
  if (outcome === "error") {
    return { tone: "error", stopped: false, head: TONE_HEAD.error, text: errorText(error, "The answer could not be finished."), hint: "", retry: true };
  }
  if (outcome === "cancelled") {
    const reason = stopReasonOf(message, cancelled);
    return {
      tone: "cannot-answer",
      stopped: true,
      head: "Stopped",
      text: (reason && STOP_REASON[reason]) || STOPPED_SENTENCE,
      hint: reason === "timeout" || reason === "budget" ? ASK_SMALLER_TEXT : "",
      retry: reason !== "replaced",
    };
  }
  if (outcome === "unfinished") {
    return { tone: "cannot-answer", stopped: true, head: "Not finished", text: "The answer stopped before it was finished.", hint: "", retry: true };
  }
  // only a finished answer can have come back empty; one still running has simply not said anything yet
  if (empty && outcome === "done") {
    return { tone: "nothing-found", stopped: false, head: TONE_HEAD["nothing-found"], text: EMPTY_ANSWER_TEXT, hint: "", retry: true };
  }
  return null;
}

const EndingCard: FC<{ notice: EndingNotice; onRetry?: () => void }> = ({ notice, onRetry }) => (
  <XbotCallout tone={notice.tone} stopped={notice.stopped}>
    <CalloutHead text={notice.head} />
    <div className="xm-xbot-outcome" role={notice.tone === "nothing-found" ? undefined : "alert"}>{notice.text}</div>
    {notice.hint ? <div className="xm-xbot-muted">{notice.hint}</div> : null}
    {notice.retry && onRetry
      ? <Button size="small" className="xm-xbot-retry" icon={<LuRotateCcw />} onClick={onRetry}>Try again</Button>
      : null}
  </XbotCallout>
);

/**
 * An empty table under a card that says nothing was found: the card has said it.
 */
export function shownBlocks(blocks: XbotMessage["blocks"], claims: ReturnType<typeof claimsOf>): XbotMessage["blocks"] {
  if (!blocks.some((block) => toneOfBlock(block, claims).tone === "nothing-found")) {
    return blocks;
  }
  return blocks.filter((block) => !(block.type === "table" && block.state === "ready" && isEmptyTable(block.config)));
}

// Text and questions have no export of their own; every other block draws Copy/CSV/JSON with itself.
const WITHOUT_EXPORT: readonly string[] = ["text", "question"];

/** True when a block other than text carries its own Copy, so one Copy per answer is enough. */
export function hasOwnExport(blocks: XbotMessage["blocks"]): boolean {
  return blocks.some((block) => block.state === "ready" && !WITHOUT_EXPORT.includes(String(block.type))
    && !(block.type === "table" && isEmptyTable(block.config)));
}

export interface XbotAnswerProps extends XbotCallbacks {
  /** either a stored message or the one a streamed turn is still building */
  message: XbotMessage;
  /** streamed turns only: current status/outcome, from turnState */
  progress?: XbotTurnProgress | null;
  /** drawn in the enlarged window, where a table has room for every column */
  wide?: boolean;
}

/** The bubble shows this many parts with data whole; the later ones start folded to a sentence. */
export const PARTS_SHOWN = 3;

/**
 * Which blocks of a long answer start folded in the bubble: the parts with data after the first
 * PARTS_SHOWN, never a text or a question (what they say is read, not looked up). Null for a short answer.
 */
export function foldingBlocks(blocks: XbotMessage["blocks"]): Set<string> | null {
  const data = blocks.filter((block) => block.state === "ready" && DATA_BLOCK_TYPES.includes(String(block.type)));
  return data.length > PARTS_SHOWN ? new Set(data.slice(PARTS_SHOWN).map((block) => block.id)) : null;
}

// Each block renders through the type registry, so an answer from a newer service still shows as text
// even for block types this build doesn't know.
export const XbotAnswer: FC<XbotAnswerProps> = ({ message, progress, loadPage, onAnswer, onOpenLink, onRetry, wide: wideProp }) => {
  // defaulted in the body: the React Compiler skips a component with a default in its destructured props
  const wide = wideProp ?? false;
  const [unfolded, setUnfolded] = useState<Record<string, boolean>>({});
  const registry = registryFor(message.schema_version);
  const outcome = progress?.outcome ?? outcomeOfMessage(message);
  const settled = outcome !== "running";
  const claims = claimsOf(message);
  const given = (Array.isArray(message.blocks) ? message.blocks : []).filter((block) => block && typeof block === "object");
  const blocks = shownBlocks(given, claims);
  const host: XbotBlockHost = {
    threadId: message.thread_id, loadPage, onAnswer, onOpenLink, claims, copyText: !hasOwnExport(blocks), wide,
  };
  const empty = outcome === "done" && given.length === 0;
  const notice = endingNotice(outcome, message.error, progress?.cancelled, empty, message);
  // a window every part shares said once on top, a long answer's later parts folded
  const said = settled ? answerWindow(blocks) : null;
  // the window's "assumed" is said once on the top line; the parts drop their note about it
  const { blocks: parts, notes: windowNotes } = said ? withoutWindowNotes(blocks, said.label) : { blocks, notes: [] as string[] };
  const assumed = Boolean(said?.assumed || windowNotes.length);
  const folding = settled && !wide ? foldingBlocks(parts) : null;
  const standing = message.standing?.text ? standingWords(message.standing.text) : "";
  // xm-xbot-look: the class the answer's look (xbot.less) hangs on
  return <div className="xm-xbot-answer xm-xbot-look" data-message-id={message.id} data-outcome={outcome}>
    {said ? <div className="xm-xbot-answer-window">
      {upperFirst(said.label.trim())}
      {assumed
        ? <span className="xm-xbot-assumed" title={windowNotes.join(" ") || "Not said in the question; a default was used."}>assumed</span>
        : null}
    </div> : null}
    <SaidWindowContext.Provider value={said ? said.label : null}>
      {parts.map((block, index) => {
        const folds = folding?.has(block.id) ?? false;
        return <XbotBlockView
          key={block.id || `block-${index}`}
          block={block}
          host={host}
          registry={registry}
          settled={settled}
          tone={toneOfBlock(block, claims)}
          folded={folds ? !unfolded[block.id] : undefined}
          onFold={folds ? (fold) => setUnfolded((was) => ({ ...was, [block.id]: !fold })) : undefined}
        />;
      })}
    </SaidWindowContext.Provider>
    {settled ? null : <XbotProgressBar progress={progress} />}
    {notice ? <EndingCard notice={notice} onRetry={onRetry} /> : null}
    {settled && standing ? <div className="xm-xbot-standing">{standing}</div> : null}
  </div>;
};

// A window every part of an answer shares is said once, at the answer's top; the parts then leave it out (SaidWindowContext, parts.tsx).

/** The windows a block's data covers, each part's own: a comparison has one a side. */
function windowsOf(block: XbotMessageBlock): (XbotWindowLabel | null)[] {
  if (block.state !== "ready") {
    return [];
  }
  const config = block.config as { window?: XbotWindowLabel | null; sides?: { window?: XbotWindowLabel | null }[] } | null;
  if (block.type === "comparison") {
    return Array.isArray(config?.sides) ? config.sides.map((side) => side?.window ?? null) : [];
  }
  return block.type === "value" || block.type === "table" || block.type === "timeseries" || block.type === "distribution"
    ? [config?.window ?? null]
    : [];
}

/** The window every dated part of the answer shares, when at least two parts have one; null otherwise. */
export function answerWindow(blocks: readonly XbotMessageBlock[]): XbotWindowLabel | null {
  const windows = blocks.flatMap(windowsOf).filter((window): window is XbotWindowLabel => Boolean(window?.label?.trim()));
  if (windows.length < 2) {
    return null;
  }
  const label = windows[0].label.trim();
  return windows.every((window) => window.label.trim() === label) ? windows[0] : null;
}

/** A part's note that the window was assumed ("No time was given, so today so far was used."). */
function isWindowNote(badge: XbotBadge | null | undefined, label: string): boolean {
  return badge?.kind === "assumed" && typeof badge.text === "string" && badge.text.toLowerCase().includes(label.trim().toLowerCase());
}

/**
 * The parts without their notes that the window said on top was assumed, and those notes: the top line then
 * says "assumed" once, with the note as its explanation, instead of each part saying it.
 */
export function withoutWindowNotes(
  blocks: readonly XbotMessageBlock[],
  label: string,
): { blocks: XbotMessageBlock[]; notes: string[] } {
  const notes: string[] = [];
  const kept = blocks.map((block) => {
    const badges = Array.isArray(block.badges) ? block.badges : [];
    const own = badges.filter((badge) => isWindowNote(badge, label));
    if (!own.length) {
      return block;
    }
    own.forEach((badge) => {
      if (!notes.includes(badge.text)) {
        notes.push(badge.text);
      }
    });
    return { ...block, badges: badges.filter((badge) => !isWindowNote(badge, label)) };
  });
  return { blocks: kept, notes };
}

/** "Carrying: app01 · CPU" -> "Next questions continue with: app01 · CPU": what the thread carries, in plain words. */
export function standingWords(text: string): string {
  return text.replace(/^Carrying:\s*/, "Next questions continue with: ");
}
